import { Duration } from 'luxon';
import caseStatuses, { CaseStatuses, caseStatusLabels } from '../../../constants/caseStatuses';
import { ValueOf } from '../../../utilities/types';

type TimelineStatus = typeof caseStatuses.OPEN
| typeof caseStatuses.IN_PROGRESS
| typeof caseStatuses.PENDING
| typeof caseStatuses.CLOSED;

type TimelineStep = {
  status: TimelineStatus;
  label: string;
  durationLabel: React.ReactNode;
  durationInMs: number;
  isCurrent: boolean;
  isReached: boolean;
};

const timelineStepVisualStates = {
  COMPLETED: 'completed',
  CURRENT: 'current',
  FUTURE: 'future',
} as const;

type TimelineStepVisualState = ValueOf<typeof timelineStepVisualStates>;

const TIMELINE_STATUSES: TimelineStatus[] = [
  caseStatuses.OPEN,
  caseStatuses.IN_PROGRESS,
  caseStatuses.PENDING,
  caseStatuses.CLOSED,
];

const CASE_DURATION_STATUSES: TimelineStatus[] = [
  caseStatuses.OPEN,
  caseStatuses.IN_PROGRESS,
  caseStatuses.PENDING,
];

const DURATION_UNIT_LABELS = { days: 'd', hours: 'h', minutes: 'm' } as const;

const formatDuration = (durationInMs: number) => {
  const shifted = Duration.fromMillis(durationInMs).shiftTo('days', 'hours', 'minutes');
  const totalMinutes = shifted.as('minutes');

  if (totalMinutes < 1) {
    return `< 1${DURATION_UNIT_LABELS.minutes}`;
  }

  const { days, hours, minutes } = shifted;
  const flooredDays = Math.floor(days);
  const flooredHours = Math.floor(hours);
  const flooredMinutes = Math.floor(minutes);

  const d = DURATION_UNIT_LABELS.days;
  const h = DURATION_UNIT_LABELS.hours;
  const m = DURATION_UNIT_LABELS.minutes;

  if (flooredDays >= 7) {
    return `${flooredDays}${d}`;
  }
  if (flooredDays >= 1) {
    return flooredHours > 0 ? `${flooredDays}${d} ${flooredHours}${h}` : `${flooredDays}${d}`;
  }
  if (flooredHours >= 1) {
    return flooredMinutes > 0 ? `${flooredHours}${h} ${flooredMinutes}${m}` : `${flooredHours}${h}`;
  }
  return `${flooredMinutes}${m}`;
};

const getTimelineSteps = ({
  status,
  statusBreakdown,
}: {
  status: CaseStatuses;
  statusBreakdown?: Record<CaseStatuses, number>;
}) => {
  const isKnownCurrentStatus = TIMELINE_STATUSES.some((stage) => stage === status);
  const currentStatus = isKnownCurrentStatus ? status as TimelineStatus : undefined;

  const steps: TimelineStep[] = TIMELINE_STATUSES.map((timelineStatus) => {
    const durationInMs = statusBreakdown?.[timelineStatus] ?? 0;

    const isReached = durationInMs > 0;
    let durationLabel: React.ReactNode = '-';

    if (isReached) {
      durationLabel = formatDuration(durationInMs);
    }

    return {
      status: timelineStatus,
      label: caseStatusLabels[timelineStatus],
      durationLabel,
      durationInMs,
      isCurrent: timelineStatus === currentStatus,
      isReached,
    };
  });

  const currentStepIndex = steps.findIndex((step) => step.isCurrent);
  const totalCaseDurationInMs = steps
    .filter((step) => CASE_DURATION_STATUSES.includes(step.status))
    .reduce((acc, step) => acc + step.durationInMs, 0);

  return {
    currentStepIndex,
    steps,
    totalCaseDurationLabel: formatDuration(totalCaseDurationInMs),
  };
};

const getTimelineStepVisualState = ({
  currentStepIndex,
  step,
  stepIndex,
}: {
  currentStepIndex: number;
  step: TimelineStep;
  stepIndex: number;
}): TimelineStepVisualState => {
  if (step.isCurrent) {
    return timelineStepVisualStates.CURRENT;
  }

  if (currentStepIndex > -1) {
    return stepIndex < currentStepIndex
      ? timelineStepVisualStates.COMPLETED
      : timelineStepVisualStates.FUTURE;
  }

  return step.isReached ? timelineStepVisualStates.COMPLETED : timelineStepVisualStates.FUTURE;
};

export {
  timelineStepVisualStates,
  getTimelineSteps,
  getTimelineStepVisualState,
};

export type {
  TimelineStep,
  TimelineStepVisualState,
};
